import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Star, MapPin, Navigation, Clock, Phone, Info, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Standard YOL HATTI provider identity card. We deliberately do NOT show the
 * provider's raw logo/cover — customers must contact the provider through the
 * platform (public_virtual_phone). All cards share the same brand visual so no
 * provider can insert bypass channels via imagery.
 */
export default function BusinessCard({ business, categories = [] }) {
  const cats = (business.category_ids || [])
    .map((cid) => categories.find((c) => c.id === cid))
    .filter(Boolean);
  const primaryCat = cats[0];
  const catNames = cats.map((c) => c.name).slice(0, 2);
  const isOpen = business.is_24_7 || business.is_open_now;
  const hasPublicPhone = !!business.public_virtual_phone;

  return (
    <article
      data-testid={`business-card-${business.id}`}
      className="group bg-card rounded-2xl border border-border overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 ease-out"
    >
      <Link to={`/firma/${business.slug || business.id}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden bg-[hsl(var(--brand-black))]">
          {/* Standardised YOL HATTI identity panel */}
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--brand-black))] via-[#171717] to-[#0a0a0a]" />
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 25% 30%, #FFC107 0px, transparent 90px), radial-gradient(circle at 75% 70%, #FFC107 0px, transparent 120px)" }} />
          <div className="absolute top-3 right-3 flex items-center gap-1 text-primary font-bold text-[10px] tracking-widest">
            <ShieldCheck className="w-3.5 h-3.5" /> YOL HATTI ONAYLI
          </div>
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {business.is_24_7 && <Badge className="bg-primary text-[hsl(var(--brand-black))] hover:bg-primary font-bold">7/24</Badge>}
            {business.is_demo && <Badge variant="secondary" className="bg-white/90 text-slate-900">DEMO</Badge>}
          </div>

          <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
            {primaryCat && (() => {
              const CatIcon = Icons[primaryCat.icon] || Icons.Truck;
              return (
                <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/40 flex items-center justify-center mb-2">
                  <CatIcon className="w-8 h-8 text-primary" />
                </div>
              );
            })()}
            <h3 className="font-extrabold text-white text-lg leading-tight line-clamp-2">{business.name}</h3>
            <div className="mt-1 text-white/70 text-xs flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{business.district}, {business.province}</span>
            </div>
          </div>

          {typeof business.distance_km === "number" && (
            <div className="absolute bottom-3 right-3 bg-primary text-[hsl(var(--brand-black))] px-2 py-0.5 rounded-full text-xs font-bold">
              {business.distance_km < 1 ? `${Math.round(business.distance_km * 1000)}m` : `${business.distance_km} km`}
            </div>
          )}
        </div>
      </Link>
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[22px]">
          {catNames.map((n) => <Badge key={n} variant="secondary" className="text-xs">{n}</Badge>)}
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-amber-500">
              <Star className="w-4 h-4 fill-current" />
              <span className="font-semibold text-sm text-foreground">{(business.rating_avg || 0).toFixed(1)}</span>
            </div>
            <span className="text-xs text-muted-foreground">({business.review_count || 0})</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
            {isOpen
              ? <Badge className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]">Açık</Badge>
              : <Badge variant="destructive">Kapalı</Badge>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {hasPublicPhone ? (
            <Button asChild size="sm" className="rounded-full press bg-[hsl(var(--brand-black))] hover:bg-[hsl(var(--brand-black))]/90 text-white" data-testid={`call-btn-${business.id}`}>
              <a href={`tel:${business.public_virtual_phone}`}><Phone className="w-4 h-4 mr-1" /> Ara</a>
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="rounded-full press" disabled data-testid={`call-soon-${business.id}`}>
              <Info className="w-4 h-4 mr-1" /> Hat yakında
            </Button>
          )}
          <Button asChild size="sm" variant="outline" className="rounded-full press" data-testid={`dir-btn-${business.id}`}>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`} target="_blank" rel="noopener noreferrer">
              <Navigation className="w-4 h-4 mr-1" /> Yol Tarifi
            </a>
          </Button>
          <Button asChild size="sm" className="col-span-2 rounded-full press bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-semibold" data-testid={`view-btn-${business.id}`}>
            <Link to={`/firma/${business.slug || business.id}`}>Firmayı Gör</Link>
          </Button>
        </div>
      </div>
    </article>
  );
}
